// Function index:
//   IsoRenderer — Renderer implementation for the isometric terrain
//       projection (Task 3): depth-sorted, bottom-anchored tile sprites
//       mounted on a single `sortableChildren` worldLayer, culled against the
//       camera's visible world rect (culling.ts::visibleWorldRect). Fog is
//       applied as a sprite tint only — there is no reveal-fade tween here,
//       that's a FlatRenderer-only cosmetic (see flat-renderer.ts). Tokens
//       (Task 4) share the same worldLayer as tiles and depth-sort against
//       them — though see the note below on what that does NOT buy you.
//       Picking (Task 5) is stage-level,
//       unlike FlatRenderer's per-tile Graphics handlers: tiles are plain
//       `eventMode: 'none'` sprites, and one pointermove/pointerdown/
//       rightdown/rightclick handler set on `ctx.app.stage` inverts the
//       camera transform (screenToHex) to find the hex under the pointer.
//   IsoRenderer.relayout          — mount tiles entering the projected
//       viewport, recycle ones leaving it.
//   IsoRenderer.redrawTile        — re-tint/re-texture one mounted tile.
//   IsoRenderer.screenToHex       — invert the camera transform then project
//       a screen point to the nearest axial hex.
//   IsoRenderer._mountedForTest   — test-only accessor to the mounted-tile map.
//   IsoRenderer._tokenForTest     — test-only accessor to a mounted token Container.
//
// Tiles come in two kinds. A terrain WITH art mounts a textured billboard
// Sprite; one WITHOUT mounts a flat-shaded Graphics hex in the ground plane
// (palette.ts::drawTile over iso/project.ts::isoHexCorners) so an uncovered
// terrain degrades to the flat map's look rather than to a hole in the world.
// hex-scene.ts's terrainTilesLoaded gate normally keeps whole uncovered worlds
// (interiors) on FlatRenderer, so the fallback exists for hexes that STREAM in
// after mount — addHexes never re-runs that gate. Both kinds share one
// worldLayer and one groundY-derived zIndex, so they depth-sort as one set.
//
// Sprites are anchored at the canvas BOTTOM (so tall art — peaks, treetops —
// extends upward out of the tile's headroom) and then lifted by
// FACE_CENTRE_FROM_BOTTOM_FRAC so the face CENTRE lands on the ground point.
//
// Tokens are NOT occluded by tall tiles in front of them, and can't be with
// this art — measured 2026-07-28, don't re-investigate. A token on the hex
// behind lands over the front tile's own left/right EDGE (front neighbours sit
// half a face width to the side), at ~220 source px above that tile's face
// centre. mountain.png is opaque there only from x=123..129 — the bare tip —
// because a peak's silhouette tapers inward exactly where the neighbouring
// token stands. Occluding a centre-standing token needs art opaque near its
// own edges that high up, i.e. a cliff/wall, not a peak. Tile-over-TILE
// occlusion works fine precisely because tiles are as wide as the lattice
// spacing; a token is ~10x narrower and sits in the gap the taper leaves.

import { Container, Graphics, Sprite, type FederatedPointerEvent, type Texture } from 'pixi.js';
import {
  FACE_CENTRE_FROM_BOTTOM_FRAC, FACE_H, FACE_W, isoHexCorners, isoProject, screenToAxial,
} from './project.js';
import { visibleWorldRect } from '../culling.js';
import {
  FOGGY_DIM, UNDISCOVERED_DIM, baseColor, dim, drawTile, type Visibility,
} from '../palette.js';
import type { Renderer, RendererCtx } from '../renderer.js';
import type { CharacterInput, HexInput, NpcInput } from '../types.js';
import { makeTokenContainer } from '../token-registry.js';

const POOL_MAX = 512;
// Tokens sit slightly forward of the hex centre so they read as standing ON the
// face rather than painted flat onto it. Measured against FACE_H (the face's
// real height): a fraction of FACE_W wouldn't track the vertical foreshortening,
// and past FACE_H/4 a token leaves the face's full-width band and starts to read
// as standing on the cell behind.
const TOKEN_LIFT = FACE_H * 0.12;

// Screen-space slop separating a right-CLICK from a right-DRAG pan — same
// guard as culling.ts's per-tile handlers, copied here because picking is
// stage-level for the iso renderer (see class doc).
const RIGHT_CLICK_SLOP_PX = 6;

function tokenKey(id: number, kind: 'character' | 'npc'): string {
  return `${id},${kind}`;
}

// A tile is a textured billboard Sprite when its terrain has art, or a
// flat-shaded Graphics hex lying in the ground plane when it doesn't. Both
// live on the same worldLayer and carry the same groundY-derived zIndex, so
// they depth-sort against each other as one set.
type IsoNode = Sprite | Graphics;

interface MountedIsoTile {
  node: IsoNode;
  terrain: string;
}

export class IsoRenderer implements Renderer {
  private readonly ctx: RendererCtx;
  private readonly terrainTexture: (terrain: string) => Texture | null;
  private readonly worldLayer: Container;
  private readonly mounted = new Map<string, MountedIsoTile>();
  private readonly spritePool: Sprite[] = [];
  private readonly graphicsPool: Graphics[] = [];
  private readonly tokens = new Map<string, Container>();
  private hoveredKey: string | null = null;
  private rightDownAt: { x: number; y: number } | null = null;

  constructor(ctx: RendererCtx, terrainTexture: (terrain: string) => Texture | null) {
    this.ctx = ctx;
    this.terrainTexture = terrainTexture;
    this.worldLayer = new Container();
    this.worldLayer.sortableChildren = true;
    // IsoRenderer is only ever constructed on the iso path (hex-scene.ts's
    // `useIso` gate), where hex-scene.ts has already added roadLayer/
    // overlayLayer/locationLayer to camera — insert worldLayer BEHIND them
    // (index 0) so those overlays render above the tiles/tokens instead of
    // being hidden underneath. FlatRenderer's own tileLayer/tokenLayer
    // insertion (addChildAt(tileLayer, 0) / addChild(tokenLayer)) is
    // untouched, so the flat path's z-order is unchanged.
    ctx.camera.addChildAt(this.worldLayer, 0);

    ctx.app.stage.eventMode = 'static';
    // Pixi hit-tests a Container through its CHILDREN or an explicit hitArea.
    // Every tile here is eventMode:'none' (picking is stage-level), so without
    // this the stage is never a hit target: pointerdown/move never fire and
    // click-to-move silently does nothing. app.screen is the live viewport
    // Rectangle — Pixi keeps it in sync on resize, so this tracks automatically.
    ctx.app.stage.hitArea = ctx.app.screen;
    ctx.app.stage.on('pointermove', this.onPointerMove);
    ctx.app.stage.on('pointerdown', this.onPointerDown);
    ctx.app.stage.on('rightdown', this.onRightDown);
    ctx.app.stage.on('rightclick', this.onRightClick);
  }

  relayout(): void {
    const rect = visibleWorldRect(this.ctx.app, this.ctx.camera);
    const needed = new Set<string>();

    for (const [key, data] of this.ctx.hexData) {
      const { sx, groundY } = isoProject(data.q, data.r);
      if (sx < rect.minX || sx > rect.maxX) continue;
      if (groundY < rect.minY || groundY > rect.maxY) continue;

      needed.add(key);
      this.applyTile(key, data, sx, groundY);
    }

    for (const [key, tile] of this.mounted) {
      if (needed.has(key)) continue;
      this.releaseNode(tile.node);
      this.mounted.delete(key);
    }
  }

  redrawTile(key: string): void {
    const data = this.ctx.hexData.get(key);
    if (!data || !this.mounted.has(key)) return;
    const { sx, groundY } = isoProject(data.q, data.r);
    this.applyTile(key, data, sx, groundY);
  }

  addToken(t: CharacterInput | NpcInput, kind: 'character' | 'npc', isSelf: boolean): void {
    const key = tokenKey(t.id, kind);
    if (this.tokens.has(key)) return;
    const foreign = kind === 'character' ? (t as CharacterInput).foreign : undefined;
    const { container } = makeTokenContainer(kind, isSelf, t.name, foreign);
    this.worldLayer.addChild(container);
    this.tokens.set(key, container);
    this.positionToken(container, t.q, t.r);
  }

  moveToken(id: number, kind: 'character' | 'npc', q: number, r: number): void {
    const container = this.tokens.get(tokenKey(id, kind));
    if (!container) return;
    this.positionToken(container, q, r);
  }

  removeToken(id: number, kind: 'character' | 'npc'): void {
    const key = tokenKey(id, kind);
    const container = this.tokens.get(key);
    if (!container) return;
    this.worldLayer.removeChild(container);
    container.destroy({ children: true });
    this.tokens.delete(key);
  }

  screenToHex(screenX: number, screenY: number): { q: number; r: number } | null {
    const wx = (screenX - this.ctx.camera.x) / this.ctx.camera.scale.x;
    const wy = (screenY - this.ctx.camera.y) / this.ctx.camera.scale.y;
    return screenToAxial(wx, wy);
  }

  destroy(): void {
    this.ctx.app.stage.off('pointermove', this.onPointerMove);
    this.ctx.app.stage.off('pointerdown', this.onPointerDown);
    this.ctx.app.stage.off('rightdown', this.onRightDown);
    this.ctx.app.stage.off('rightclick', this.onRightClick);
    for (const tile of this.mounted.values()) tile.node.destroy();
    this.mounted.clear();
    for (const sprite of this.spritePool) sprite.destroy();
    this.spritePool.length = 0;
    for (const g of this.graphicsPool) g.destroy();
    this.graphicsPool.length = 0;
    for (const container of this.tokens.values()) container.destroy({ children: true });
    this.tokens.clear();
    this.worldLayer.destroy({ children: true });
  }

  _mountedForTest(): Map<string, MountedIsoTile> {
    return this.mounted;
  }

  _tokenForTest(id: number, kind: 'character' | 'npc'): Container | undefined {
    return this.tokens.get(tokenKey(id, kind));
  }

  private positionToken(container: Container, q: number, r: number): void {
    const { sx, groundY } = isoProject(q, r);
    container.x = sx;
    container.y = groundY - TOKEN_LIFT;
    // Just above its own tile (Math.round(groundY)) so a tile further front
    // — larger groundY, larger zIndex — draws over it.
    container.zIndex = Math.round(groundY) + 1;
  }

  // Bound instance properties (not prototype methods) so the same reference
  // can be passed to both `.on` (constructor) and `.off` (destroy).
  private readonly onPointerMove = (e: FederatedPointerEvent): void => {
    const hex = this.screenToHex(e.global.x, e.global.y);
    const key = hex ? `${hex.q},${hex.r}` : null;
    const nextKey = key !== null && this.ctx.hexData.has(key) ? key : null;
    if (nextKey === this.hoveredKey) return;
    if (this.hoveredKey !== null) this.ctx.onTileHoverOut(this.hoveredKey);
    this.hoveredKey = nextKey;
    if (nextKey !== null && hex) this.ctx.onTileHover(hex.q, hex.r, nextKey);
  };

  private readonly onPointerDown = (e: FederatedPointerEvent): void => {
    if (e.button !== 0) return;
    const hex = this.screenToHex(e.global.x, e.global.y);
    if (!hex) return;
    const key = `${hex.q},${hex.r}`;
    if (!this.ctx.hexData.has(key)) return;
    this.ctx.onTileClick(hex.q, hex.r);
  };

  private readonly onRightDown = (e: FederatedPointerEvent): void => {
    this.rightDownAt = { x: e.clientX, y: e.clientY };
  };

  private readonly onRightClick = (e: FederatedPointerEvent): void => {
    if (
      this.rightDownAt &&
      Math.hypot(e.clientX - this.rightDownAt.x, e.clientY - this.rightDownAt.y) > RIGHT_CLICK_SLOP_PX
    ) {
      return;
    }
    const hex = this.screenToHex(e.global.x, e.global.y);
    if (!hex) return;
    const key = `${hex.q},${hex.r}`;
    if (!this.ctx.hexData.has(key) || !this.ctx.discoveredKeys.has(key)) return;
    this.ctx.onTileRightClick(hex.q, hex.r);
  };

  // Mount-or-update one tile, swapping the node kind if the terrain's art
  // availability changed since it was mounted (a streamed hex, or an atlas
  // that resolved late).
  private applyTile(key: string, data: HexInput, sx: number, groundY: number): void {
    const tex = this.terrainTexture(data.terrain);
    const existing = this.mounted.get(key);
    if (existing && existing.node instanceof Sprite !== (tex !== null)) {
      this.releaseNode(existing.node);
      this.mounted.delete(key);
    }

    const tile = this.mounted.get(key) ?? this.mountTile(key, tex !== null);
    tile.terrain = data.terrain;
    if (tex) this.applySprite(tile.node as Sprite, tex, key, sx, groundY);
    else this.applyVector(tile.node as Graphics, data, key, sx, groundY);
  }

  private mountTile(key: string, textured: boolean): MountedIsoTile {
    const node: IsoNode = textured ? this.acquireSprite() : this.acquireGraphics();
    if (node instanceof Sprite) node.anchor.set(0.5, 1);
    node.eventMode = 'none'; // picking is stage-level (Task 5)
    this.worldLayer.addChild(node);
    const tile: MountedIsoTile = { node, terrain: '' };
    this.mounted.set(key, tile);
    return tile;
  }

  private applySprite(sprite: Sprite, tex: Texture, key: string, sx: number, groundY: number): void {
    const discovered = this.ctx.discoveredKeys.has(key);
    const vis: Visibility = this.ctx.visibilityFor(key, discovered);
    sprite.texture = tex;
    sprite.width = FACE_W;
    sprite.height = FACE_W * (tex.height / tex.width);
    sprite.x = sx;
    // Anchor is the canvas bottom, but the face centre is what belongs on the
    // ground point — the rest of the canvas is headroom for peaks/treetops.
    // Lift by the face-centre inset so the drawn hex lands on its lattice cell.
    sprite.y = groundY + FACE_CENTRE_FROM_BOTTOM_FRAC * sprite.height;
    sprite.zIndex = Math.round(groundY);
    sprite.tint = vis === 'visible' ? 0xffffff : dim(0xffffff, vis === 'foggy' ? FOGGY_DIM : UNDISCOVERED_DIM);
  }

  // No art for this terrain — draw the hex's ground-plane outline flat-shaded
  // in its terrain colour, so an uncovered terrain degrades to the flat map's
  // look rather than to a hole. isoHexCorners returns absolute world coords;
  // rebase them onto the ground point so node.x/y means the same thing here as
  // it does for a sprite tile.
  private applyVector(g: Graphics, data: HexInput, key: string, sx: number, groundY: number): void {
    const discovered = this.ctx.discoveredKeys.has(key);
    const vis: Visibility = this.ctx.visibilityFor(key, discovered);
    const local = isoHexCorners(data.q, data.r).map((c) => ({ x: c.x - sx, y: c.y - groundY }));
    g.clear();
    drawTile(g, local, baseColor(data.terrain, vis));
    g.x = sx;
    g.y = groundY;
    g.zIndex = Math.round(groundY);
  }

  private acquireSprite(): Sprite {
    return this.spritePool.pop() ?? new Sprite();
  }

  private acquireGraphics(): Graphics {
    return this.graphicsPool.pop() ?? new Graphics();
  }

  private releaseNode(node: IsoNode): void {
    this.worldLayer.removeChild(node);
    if (node instanceof Sprite) {
      node.tint = 0xffffff;
      if (this.spritePool.length < POOL_MAX) this.spritePool.push(node);
      else node.destroy();
    } else {
      node.clear();
      if (this.graphicsPool.length < POOL_MAX) this.graphicsPool.push(node);
      else node.destroy();
    }
  }
}
